import React, { useMemo, useRef, useState } from "react";
import { Button, Dropdown, Menu, Popconfirm, Skeleton, Tag } from "antd";
import { HolderOutlined, MoreOutlined } from "@ant-design/icons";
import DueDatePill from "./DueDatePill";

const PRIORITY_COLOR = { High: "red", Medium: "gold", Low: "green" };

const STATUS_COLUMNS = [
  {
    key: "Draft",
    label: "Draft",
    headerClass: "border-t-4 border-t-blue-500 bg-blue-50/60",
    badgeClass: "bg-blue-100 text-blue-800",
    dropHighlight: "bg-blue-50 ring-2 ring-inset ring-blue-300",
  },
  {
    key: "Open",
    label: "Open",
    headerClass: "border-t-4 border-t-orange-500 bg-orange-50/60",
    badgeClass: "bg-orange-100 text-orange-800",
    dropHighlight: "bg-orange-50 ring-2 ring-inset ring-orange-300",
  },
  {
    key: "Closed",
    label: "Closed",
    headerClass: "border-t-4 border-t-gray-400 bg-gray-50/80",
    badgeClass: "bg-gray-200 text-gray-700",
    dropHighlight: "bg-gray-100 ring-2 ring-inset ring-gray-400",
  },
];

const compareTasks = (a, b, sortField, sortOrder) => {
  const dir = sortOrder === "desc" ? -1 : 1;
  const av = a[sortField];
  const bv = b[sortField];

  if (sortField === "number") {
    return (Number(av) - Number(bv)) * dir;
  }
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
};

const TaskKanbanCard = ({
  task,
  isSelected,
  isDragging,
  isUpdating,
  canDrag,
  canManageTasks,
  currentUser,
  notifyingTaskId,
  onSelect,
  onUpdate,
  onNotify,
  onDelete,
  onDragStart,
  onDragEnd,
}) => {
  const suppressClickRef = useRef(false);
  const ballInCourtIds = task.ball_in_court_ids || [];
  const assigneeIds = task.assignee_ids || [];
  const isInBallInCourt = ballInCourtIds.some((id) => String(id) === String(currentUser?.id));
  const isAssignee = assigneeIds.some((id) => String(id) === String(currentUser?.id));
  const isTaskManager = String(task.task_manager_id) === String(currentUser?.id);
  const canRespond = isInBallInCourt || isAssignee || isTaskManager;

  const actionMenu = (
    <Menu>
      <Menu.Item
        key="update"
        onClick={({ domEvent }) => {
          domEvent.stopPropagation();
          onUpdate(task);
        }}
      >
        Update
      </Menu.Item>
      <Menu.Item
        key="notify"
        disabled={notifyingTaskId === task.id}
        onClick={({ domEvent }) => {
          domEvent.stopPropagation();
          onNotify(task);
        }}
      >
        Notify Users
      </Menu.Item>
      <Menu.Item key="delete">
        <Popconfirm
          title="Are you sure you want to delete this task?"
          onConfirm={(e) => {
            e?.stopPropagation?.();
            onDelete(task.id);
          }}
          okText="Yes"
          cancelText="No"
        >
          <span className="text-red-500" onClick={(e) => e.stopPropagation()}>
            Delete
          </span>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const handleDragStart = (e) => {
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    suppressClickRef.current = true;
    e.dataTransfer.setData("text/task-id", String(task.id));
    e.dataTransfer.setData("text/from-status", task.status);
    e.dataTransfer.effectAllowed = "move";
    onDragStart(task.id);
  };

  const handleDragEnd = () => {
    onDragEnd();
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handleClick = () => {
    if (suppressClickRef.current) return;
    onSelect(task);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={canDrag && !isUpdating}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`w-full text-left rounded-lg border bg-white p-3 shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
        isDragging ? "opacity-40 scale-[0.98]" : "hover:shadow-md"
      } ${
        isSelected
          ? "border-blue-500 ring-2 ring-blue-200"
          : "border-gray-200 hover:border-gray-300"
      } ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${
        isUpdating ? "opacity-60 pointer-events-none" : ""
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        {canDrag && (
          <HolderOutlined className="text-gray-400 mt-0.5 shrink-0 text-xs" aria-hidden />
        )}
        <div className="font-medium text-sm text-gray-900 line-clamp-2 leading-snug flex-1 min-w-0">
          <span className="text-gray-500 font-normal">#{task.number}</span>
          {" — "}
          {task.title}
        </div>
        {canManageTasks && (
          <Dropdown overlay={actionMenu} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="!-mt-1 !-mr-1 shrink-0"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}
      </div>

      {(!canManageTasks && canRespond) || isUpdating ? (
        <div className={`flex flex-wrap items-center gap-1.5 mb-2 ${canDrag ? "pl-4" : ""}`}>
          {!canManageTasks && canRespond && (
            <Tag color="blue" className="m-0 text-xs">
              Respond
            </Tag>
          )}
          {isUpdating && (
            <Tag className="m-0 text-xs">Updating…</Tag>
          )}
        </div>
      ) : null}

      <div className={`flex items-start justify-between gap-2 ${canDrag ? "pl-4" : ""}`}>
        {task.formatted_ball_in_court_users ? (
          <div
            className="text-xs text-gray-500 line-clamp-2 min-w-0 flex-1"
            title={task.formatted_ball_in_court_users}
          >
            <span className="font-medium text-gray-600">Ball: </span>
            {task.formatted_ball_in_court_users}
          </div>
        ) : (
          <div className="flex-1 min-w-0" />
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          {task.due_date ? (
            <DueDatePill dateString={task.due_date} status={task.status} />
          ) : null}
          {task.priority ? (
            <Tag color={PRIORITY_COLOR[task.priority]} className="m-0 text-xs">
              {task.priority}
            </Tag>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const TaskKanbanBoard = ({
  tasks,
  loading,
  total,
  selectedTaskId,
  sortField,
  sortOrder,
  canManageTasks,
  currentUser,
  notifyingTaskId,
  updatingTaskId,
  onSelect,
  onUpdate,
  onNotify,
  onDelete,
  onStatusChange,
}) => {
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragCounterRef = useRef({});

  const tasksByStatus = useMemo(() => {
    const groups = { Draft: [], Open: [], Closed: [] };
    const sorted = [...(tasks || [])].sort((a, b) =>
      compareTasks(a, b, sortField || "number", sortOrder || "asc"),
    );
    sorted.forEach((task) => {
      if (groups[task.status]) {
        groups[task.status].push(task);
      }
    });
    return groups;
  }, [tasks, sortField, sortOrder]);

  const handleColumnDragEnter = (columnKey) => {
    dragCounterRef.current[columnKey] = (dragCounterRef.current[columnKey] || 0) + 1;
    setDragOverColumn(columnKey);
  };

  const handleColumnDragLeave = (columnKey) => {
    dragCounterRef.current[columnKey] = Math.max(0, (dragCounterRef.current[columnKey] || 0) - 1);
    if (dragCounterRef.current[columnKey] === 0) {
      setDragOverColumn((current) => (current === columnKey ? null : current));
    }
  };

  const handleColumnDrop = (columnKey, e) => {
    e.preventDefault();
    dragCounterRef.current = {};
    setDragOverColumn(null);
    setDraggingTaskId(null);

    if (!canManageTasks) return;

    const taskId = e.dataTransfer.getData("text/task-id");
    const fromStatus = e.dataTransfer.getData("text/from-status");
    if (!taskId || fromStatus === columnKey) return;

    onStatusChange(taskId, columnKey);
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 w-full bg-gray-50/80">
      {canManageTasks && (
        <div className="px-4 py-1.5 text-xs text-gray-500 bg-white border-b border-gray-200 shrink-0">
          Drag a card to another column to change its status
        </div>
      )}

      {total > (tasks?.length || 0) && (
        <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 shrink-0">
          Showing {tasks?.length || 0} of {total} tasks. Refine search or filters to see more.
        </div>
      )}

      <div className="flex-1 min-h-0 w-full overflow-x-auto overflow-y-hidden">
        <div
          className="grid h-full w-full min-w-[840px]"
          style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
        >
        {STATUS_COLUMNS.map((column, columnIndex) => {
          const columnTasks = tasksByStatus[column.key] || [];
          const isDropTarget = dragOverColumn === column.key && draggingTaskId;
          const isLastColumn = columnIndex === STATUS_COLUMNS.length - 1;

          return (
            <div
              key={column.key}
              className={`flex flex-col h-full min-h-0 min-w-0 ${
                isLastColumn ? "" : "border-r border-gray-200"
              }`}
            >
              <div
                className={`px-4 py-3 border-b border-gray-200 shrink-0 flex items-center justify-between ${column.headerClass}`}
              >
                <h3 className="text-sm font-semibold text-gray-800 m-0 uppercase tracking-wide">
                  {column.label}
                </h3>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${column.badgeClass}`}
                >
                  {columnTasks.length}
                </span>
              </div>

              <div
                className={`flex-1 overflow-y-auto p-4 space-y-3 min-h-0 transition-colors ${
                  isDropTarget ? column.dropHighlight : "bg-white/40"
                }`}
                onDragOver={(e) => {
                  if (!canManageTasks) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDragEnter={() => {
                  if (draggingTaskId) handleColumnDragEnter(column.key);
                }}
                onDragLeave={() => handleColumnDragLeave(column.key)}
                onDrop={(e) => handleColumnDrop(column.key, e)}
              >
                {columnTasks.length ? (
                  columnTasks.map((task) => (
                    <TaskKanbanCard
                      key={task.id}
                      task={task}
                      isSelected={task.id === selectedTaskId}
                      isDragging={draggingTaskId === task.id}
                      isUpdating={updatingTaskId === task.id}
                      canDrag={canManageTasks}
                      canManageTasks={canManageTasks}
                      currentUser={currentUser}
                      notifyingTaskId={notifyingTaskId}
                      onSelect={onSelect}
                      onUpdate={onUpdate}
                      onNotify={onNotify}
                      onDelete={onDelete}
                      onDragStart={setDraggingTaskId}
                      onDragEnd={() => setDraggingTaskId(null)}
                    />
                  ))
                ) : (
                  <div
                    className={`text-center text-gray-400 text-xs py-8 px-2 rounded-lg border-2 border-dashed ${
                      isDropTarget ? "border-blue-300 text-blue-500" : "border-transparent"
                    }`}
                  >
                    {isDropTarget ? `Drop to mark as ${column.label}` : `No ${column.label.toLowerCase()} tasks`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default TaskKanbanBoard;
